import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { TenantWorkspaceApi, type BusinessProfile } from '@/modules/tenants/services/workspace-api';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { ErrorState } from '@/shared/feedback';
import { FiImage, FiSave, FiRefreshCw, FiAlertCircle, FiX, FiSettings } from 'react-icons/fi';
import { renderIcon } from '@/shared/layout/icon-registry';

export function BrandingPage(): ReactNode {
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const errorMessage = error?.message ?? 'An unknown error occurred';
  const [logoDocumentId, setLogoDocumentId] = useState<string | null>(null);
  const [bannerDocumentId, setBannerDocumentId] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState<string>('#000000');
  const [hasColorChange, setHasColorChange] = useState(false);

  const loadProfile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await TenantWorkspaceApi.getProfile();
      setProfile(data);
      setLogoDocumentId(data.logoDocumentId ?? null);
      setBannerDocumentId(data.bannerDocumentId);
      setPrimaryColor(data.primaryColor || '#000000');
      setHasColorChange(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load branding'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleAttachLogo = async (documentId: string | null) => {
    setIsSaving(true);
    setError(null);
    try {
      await TenantWorkspaceApi.attachLogo(documentId);
      setLogoDocumentId(documentId);
      await loadProfile();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to attach logo'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAttachBanner = async (documentId: string | null) => {
    setIsSaving(true);
    setError(null);
    try {
      await TenantWorkspaceApi.attachBanner(documentId);
      setBannerDocumentId(documentId);
      await loadProfile();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to attach banner'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveColor = async () => {
    if (!hasColorChange) return;
    setIsSaving(true);
    setError(null);
    try {
      await TenantWorkspaceApi.setPrimaryColor(primaryColor);
      setHasColorChange(false);
      await loadProfile();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to save color'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorChange = (color: string) => {
    setPrimaryColor(color);
    setHasColorChange(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return <ErrorState title="Could not load branding" message={errorMessage} onRetry={loadProfile} />;
  }

  if (!profile) {
    return <ErrorState title="No profile found" message="This tenant does not have a business profile yet." />;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FiSettings className="h-5 w-5" />
            Branding
          </h2>
          <p className="text-sm text-content-muted mt-1">
            Manage logo, banner, and brand color. Assets must be uploaded via the media API first.
          </p>
        </div>
        <Button variant="secondary" onClick={loadProfile} disabled={isLoading}>
          <FiRefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && <ErrorState title="Operation failed" message={errorMessage} />}

      {/* Logo Section */}
      <section className="rounded-lg border border-border p-6 space-y-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <FiImage className="h-5 w-5" />
          Logo
        </h3>
        <p className="text-sm text-content-muted">
          Attach a logo document ID. The asset must be a raster image (JPEG, PNG, WebP, AVIF) that has passed scanning.
          Logo is not automatically published — it requires <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">media_asset.publication_state = 'published'</code>.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <div className="w-32 h-32 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-background relative overflow-hidden">
            {logoDocumentId ? (
              <div className="text-center p-2">
                <p className="text-xs text-content-muted">Logo Attached</p>
                <p className="text-xs font-mono text-content">{logoDocumentId.slice(0, 8)}…</p>
              </div>
            ) : (
              <FiImage className="h-8 w-8 text-content-muted" />
            )}
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex gap-2">
              <Input
                label="Document ID"
                value={logoDocumentId || ''}
                onChange={(e) => setLogoDocumentId(e.currentTarget.value || null)}
                placeholder="Enter media document UUID"
                className="flex-1"
              />
              <Button
                onClick={() => handleAttachLogo(logoDocumentId)}
                loading={isSaving}
                disabled={isSaving}
              >
                {logoDocumentId ? 'Update' : 'Attach'}
              </Button>
              {logoDocumentId && (
                <Button variant="secondary" onClick={() => handleAttachLogo(null)}>
                  <FiX className="h-4 w-4 mr-1" /> Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-content-muted">
              Current: {logoDocumentId ? `${logoDocumentId.slice(0, 8)}…` : 'None'}
            </p>
          </div>
        </div>
      </section>

      {/* Banner Section */}
      <section className="rounded-lg border border-border p-6 space-y-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <FiImage className="h-5 w-5" />
          Banner
        </h3>
        <p className="text-sm text-content-muted">
          Attach a banner document ID. Same requirements as logo — raster image, scanned, published for public display.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <div className="w-64 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-background relative overflow-hidden">
            {bannerDocumentId ? (
              <div className="text-center p-2">
                <p className="text-xs text-content-muted">Banner Attached</p>
                <p className="text-xs font-mono text-content">{bannerDocumentId.slice(0, 8)}…</p>
              </div>
            ) : (
              <FiImage className="h-8 w-8 text-content-muted" />
            )}
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex gap-2">
              <Input
                label="Document ID"
                value={bannerDocumentId || ''}
                onChange={(e) => setBannerDocumentId(e.currentTarget.value || null)}
                placeholder="Enter media document UUID"
                className="flex-1"
              />
              <Button
                onClick={() => handleAttachBanner(bannerDocumentId)}
                loading={isSaving}
                disabled={isSaving}
              >
                {bannerDocumentId ? 'Update' : 'Attach'}
              </Button>
              {bannerDocumentId && (
                <Button variant="secondary" onClick={() => handleAttachBanner(null)}>
                  <FiX className="h-4 w-4 mr-1" /> Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-content-muted">
              Current: {bannerDocumentId ? `${bannerDocumentId.slice(0, 8)}…` : 'None'}
            </p>
          </div>
        </div>
      </section>

      {/* Primary Color Section */}
      <section className="rounded-lg border border-border p-6 space-y-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <FiSettings className="h-5 w-5" />
          Primary Brand Color
        </h3>
        <p className="text-sm text-content-muted">
          Set the primary brand color in <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">#RRGGBB</code> format.
          Used for UI accents, buttons, and public profile theming.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <div className="flex items-center gap-4">
            <Input
              label="Hex Color"
              value={primaryColor}
              onChange={(e) => handleColorChange(e.currentTarget.value)}
              pattern="^#[0-9A-Fa-f]{6}$"
              maxLength={7}
              className="w-40 font-mono"
            />
            <div
              className="w-12 h-12 rounded-lg border-2 border-border flex-shrink-0"
              style={{ backgroundColor: primaryColor }}
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleSaveColor}
              loading={isSaving}
              disabled={!hasColorChange || isSaving}
            >
              <FiSave className="h-4 w-4 mr-2" />
              Save Color
            </Button>
            <Button variant="secondary" onClick={() => handleColorChange('#000000')}>
              Reset to Default
            </Button>
          </div>

          <p className="text-xs text-content-muted">
            Current saved: {profile.primaryColor || 'Not set'}
          </p>
        </div>
      </section>

      {/* Current Values */}
      <details className="rounded-lg border border-border p-6">
        <summary className="cursor-pointer font-medium text-content">Current Saved Values (Read-Only)</summary>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
          <ProfileField label="Logo Document" value={profile.logoDocumentId ? `${profile.logoDocumentId.slice(0, 8)}…` : '—'} />
          <ProfileField label="Banner Document" value={profile.bannerDocumentId ? `${profile.bannerDocumentId.slice(0, 8)}…` : '—'} />
          <ProfileField label="Primary Color" value={profile.primaryColor || '—'} />
          <ProfileField label="Version" value={String(profile.version)} />
          <ProfileField label="Tenant ID" value={profile.tenantId} />
        </div>
      </details>
    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-content-muted uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-content font-mono">{value}</dd>
    </div>
  );
}